import { withErrorBoundary } from "@sentry/nextjs";
import { gql } from "graphql-request";
import { VFC } from "react";
import styled from "styled-components";
import { Image } from "components";
import { useAnchor } from "hooks";
import { Hero2SubsectionContent } from "types/graphql/generated";

interface Props {
  className?: string;
  content: Hero2SubsectionContent;
}

const Hero2Subsection: VFC<Props> = withErrorBoundary(
  ({
    content: {
      description,
      image: {
        data: { image },
      },
    },
  }) => {
    useAnchor("hero-subsection2");

    return (
      <StyledHero2 className="hero-subsection2 Content">
        <div className="Hero2__Content">
          <div className="Hero2__descriptionContainer">
            <div className="Hero2__description Font_32_40_600 xl:Font_24_32_600 ">{description}</div>
          </div>
          <div className="Hero2__image__container">
            <Image {...image} fill className="Hero2__image" sizes="30vw" />
          </div>
        </div>
      </StyledHero2>
    );
  },
  {}
);

const StyledHero2 = styled.section`
  position: relative;
  margin-top: -296px;
  z-index: 10;
  padding-top: 64px;

  .Hero2__Content {
    position: relative;
    display: flex;
    padding: 0;
    background: ${({ theme }) => theme.colors.coral["40"]};
  }

  .Hero2__descriptionContainer {
    display: flex;
    align-items: center;
    padding: 100px 64px;
  }

  .Hero2__border {
    border-left: 3px solid ${({ theme }) => theme.colors.coral["80"]};
    height: 184px;
    margin: auto 0;
  }

  .Hero2__description {
    position: relative;
    display: flex;
    align-self: center;
    padding-left: 40px;
    color: ${({ theme }) => theme.colors.coral["80"]};
  }

  .Hero2__description::before {
    content: "";
    position: absolute;
    width: 3px;
    height: calc(100% - 16px);
    left: 0;
    top: 8px;
    z-index: 0;
    background-color: ${({ theme }) => theme.colors.coral["80"]};
  }

  .Hero2__image {
    &__container {
      min-width: 400px;
      position: relative;
      min-height: 400px;
    }

    object-fit: cover;
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.xl}px`}) {
    .Hero2__descriptionContainer {
      padding: 80px 48px;
    }

    .Hero2__border {
      height: 144px;
    }

    .Hero2__image {
      &__container {
        min-width: 320px;
        min-height: 320px;
      }
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.lg}px`}) {
    &.Content {
      padding-left: 0;
      padding-right: 0;
    }

    .Hero2__descriptionContainer {
      padding: 48px 32px;
    }

    .Hero2__description {
      padding-left: 0;
    }

    .Hero2__description::before {
      display: none;
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.md}px`}) {
    margin-top: -64px;

    .Hero2__descriptionContainer {
      padding: 80px 24px;
    }

    .Hero2__image {
      &__container {
        display: none;
      }
    }
  }
`;

gql`
  fragment Hero2SubsectionContent on ComponentHeroHero2Subsection {
    description
    image {
      data {
        image: attributes {
          src: hash
          alt: alternativeText
        }
      }
    }
  }
`;

export { Hero2Subsection };
export type { Props as Hero2SubsectionProps };
